import { useRef, useState, useLayoutEffect, useCallback, type ReactNode } from 'react'
import { CheckCircle2, AlertCircle } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from './button'

export type DialogStatus = 'idle' | 'success' | 'error'

export interface DialogResultTransitionProps {
  /** Current status of the dialog */
  status: DialogStatus
  /** Form content rendered when status is 'idle' */
  children: ReactNode
  /** Message shown on success */
  successMessage?: string
  /** Message shown on error */
  errorMessage?: string
  /** Error description shown below the error message */
  errorDescription?: string
  /** Called when "Try again" is clicked in the error state */
  onRetry?: () => void
  /** Called when "Cancel" is clicked in the error state */
  onClose?: () => void
}

const FADE_VARIANTS = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -8 },
}

export function DialogResultTransition({
  status,
  children,
  successMessage = 'Success',
  errorMessage = 'Something went wrong',
  errorDescription = 'Please try again.',
  onRetry,
  onClose,
}: DialogResultTransitionProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const [lockedHeight, setLockedHeight] = useState<number | null>(null)

  // Measure and lock the form height right before leaving idle
  useLayoutEffect(() => {
    if (status !== 'idle' && contentRef.current && lockedHeight === null) {
      setLockedHeight(contentRef.current.offsetHeight)
    }
    if (status === 'idle') {
      setLockedHeight(null)
    }
  }, [status, lockedHeight])

  const handleRetry = useCallback(() => {
    setLockedHeight(null)
    onRetry?.()
  }, [onRetry])

  return (
    <div
      style={lockedHeight !== null ? { height: lockedHeight, overflow: 'hidden' } : undefined}
      className="transition-[height] duration-200"
    >
      <AnimatePresence mode="wait" initial={false}>
        {status === 'idle' && (
          <motion.div
            key="form"
            ref={contentRef}
            variants={FADE_VARIANTS}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
          >
            {children}
          </motion.div>
        )}

        {status === 'success' && (
          <motion.div
            key="success"
            variants={FADE_VARIANTS}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.25 }}
            className="flex flex-col items-center justify-center gap-4"
            style={lockedHeight !== null ? { height: lockedHeight } : undefined}
          >
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
            >
              <CheckCircle2 className="h-12 w-12 text-green-500" />
            </motion.div>
            <p className="text-sm font-medium text-foreground">{successMessage}</p>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div
            key="error"
            variants={FADE_VARIANTS}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.25 }}
            className="flex flex-col items-center justify-center gap-4"
            style={lockedHeight !== null ? { height: lockedHeight } : undefined}
          >
            <motion.div
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20, delay: 0.1 }}
            >
              <AlertCircle className="h-12 w-12 text-destructive" />
            </motion.div>
            <p className="text-sm font-medium text-foreground">{errorMessage}</p>
            <p className="text-xs text-muted-foreground">{errorDescription}</p>
            <div className="flex gap-2 pt-2">
              {onClose && (
                <Button variant="ghost" onClick={onClose} className="font-medium">
                  Cancel
                </Button>
              )}
              {onRetry && (
                <Button onClick={handleRetry} className="rounded-full px-6">
                  Try again
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}
